import { FragmentType } from '../gql'
import { Product } from '../gql/graphql'
import { ProductCell, ProductCell_ProductFragment } from '../views/OrderDetails/components/ProductCell'
import Modal from './Modal'

import { useEffect, useRef, useState } from 'react'
import { Button } from './shadcn/Button'

export interface SerialNumbersModalProps {
  isOpen: boolean
  onClose: () => void
  serialNumberPicking?: SerialNumbersPickingParams
  onSerialNumbersPicked: (serialNumbers: string[]) => void
}

export interface SerialNumbersHookProps {
  onSerialNumbersPicked?: (serialNumbers: string[]) => void
}

export interface SerialNumbersPickingParams {
  existingSerialNumbers?: string[]
  totalAmount?: number
  product?: FragmentType<typeof ProductCell_ProductFragment>
}

export function useSerialNumbersModal({ onSerialNumbersPicked }: SerialNumbersHookProps = {}): {
  serialNumbersModalProps: SerialNumbersModalProps
  open: (
    amount: number,
    product: FragmentType<typeof ProductCell_ProductFragment>,
    onSerialNumbersPicked?: (serialNumbers: string[]) => void,
  ) => void
  close: () => void
} {
  const [isOpen, setIsOpen] = useState(false)
  const [serialNumberPicking, setSerialNumberPicking] = useState<SerialNumbersPickingParams | undefined>(undefined)

  const callbackRef = useRef<((serialNumbers: string[]) => void) | undefined>(undefined)
  const handleSerialNumbersPicked = (serialNumbers: string[]) => {
    callbackRef.current?.(serialNumbers)
    onSerialNumbersPicked?.(serialNumbers)
    setIsOpen(false)
    setSerialNumberPicking(undefined)
  }

  return {
    serialNumbersModalProps: {
      isOpen,
      onClose: () => setIsOpen(false),
      serialNumberPicking,
      onSerialNumbersPicked: handleSerialNumbersPicked,
    },
    open: (
      amount: number,
      product: FragmentType<typeof ProductCell_ProductFragment>,
      onSerialNumbersPicked?: (serialNumbers: string[]) => void,
    ) => {
      setIsOpen(true)
      setSerialNumberPicking({
        existingSerialNumbers: [],
        totalAmount: amount,
        product,
      })
      if (onSerialNumbersPicked) {
        callbackRef.current = onSerialNumbersPicked
      }
    },
    close: () => setIsOpen(false),
  }
}

export default function SerialNumbersModal({
  isOpen,
  onClose,
  serialNumberPicking,
  onSerialNumbersPicked,
}: SerialNumbersModalProps) {
  const [items, setItems] = useState<string[]>([])
  const [inputValue, setInputValue] = useState('')
  const inputRef = useRef<HTMLDivElement>(null)

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const inputValue = (e.target as HTMLDivElement).innerText
    if (e.key === 'Enter') {
      e.preventDefault()
      if (items.length >= serialNumberPicking?.totalAmount) {
        return
      }
      if (inputValue.trim()) {
        setItems([...items, inputValue.trim()])
        setInputValue('')
        if (inputRef.current) {
          inputRef.current.innerHTML = ''
        }
      }
    } else if (e.key === 'Backspace' && (inputValue === '' || inputValue == '\n') && items.length > 0) {
      e.preventDefault()
      setItems(items.slice(0, -1))
    }
  }

  const removeItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index))
  }

  const handleInput = (e: React.FormEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement
    setInputValue(target.innerText)
  }

  const handleClose = () => {
    onClose()
    onSerialNumbersPicked?.(items)
    setItems([])
  }

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        inputRef.current?.focus()
      }, 100)
    } else {
      setItems([])
    }
  }, [isOpen])

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Seriová čísla" closable={false}>
      <div className="flex flex-col gap-2 mb-4">
        <ProductCell product={serialNumberPicking?.product} />
        <div className="text-sm text-gray-500">
          Zadejte seriová čísla pro <b>{serialNumberPicking?.totalAmount} ks.</b>
        </div>
      </div>
      <div
        className="border rounded-xl p-2 gap-2 cursor-text flex flex-wrap items-start focus-within:outline focus-within:outline-1 focus-within:outline-gray-600"
        onClick={() => inputRef.current?.focus()}>
        {items.map((item, index) => (
          <span key={index} className="bg-gray-200 rounded-lg px-2 py-1">
            {item}
            <button
              onClick={e => {
                e.stopPropagation()
                removeItem(index)
              }}
              className="ml-2 text-red-500 font-bold">
              ×
            </button>
          </span>
        ))}
        <div
          ref={inputRef}
          className="inline-block vertical-align-middle outline-none px-2 py-1 h-24"
          contentEditable={items.length < serialNumberPicking?.totalAmount}
          onKeyDown={handleKeyDown}
          onInput={handleInput}
          suppressContentEditableWarning={true}
        />
      </div>
      <div className="text-sm text-gray-500 mt-2 relative bottom-[34px] left-[12px]">
        Zbývá: <b>{Math.max(0, (serialNumberPicking?.totalAmount || 0) - items.length)}</b>
      </div>
      <div className="flex justify-end mt-4">
        <Button
          onClick={() => handleClose()}
          disabled={items.length !== serialNumberPicking?.totalAmount}
          className="w-44">
          Pokračovat
        </Button>
      </div>
    </Modal>
  )
}
